import { styled } from '@mui/material';
import { FrontTooltip, Skeleton } from 'src/ui-components';
import { Numbers } from 'src/ui-components/helpers/formatters';
import { TrendIcon } from 'src/ui-components/icons';

import type { IBlockData } from '../../../helpers/types';
import { useGetValues } from '../../../hooks/useGetValues';

export const BlockData = ({ block }: { block: IBlockData }) => {
  const {
    value,
    percentValue,
    previousDates,
    previousValue,
    positiveDynamics,
    customTooltip,
    isLoading,
  } = useGetValues({
    ...block,
  });

  return (
    <FrontTooltip
      disabled={isLoading}
      placement="top"
      arrow
      title={
        <TooltipContainer>
          <TooltipRow
            dangerouslySetInnerHTML={{
              __html: customTooltip || block.tooltipTitle || block.title,
            }}
          ></TooltipRow>
          {!block.onlyCustomTooltip && (
            <TooltipRow>
              <TooltipText>
                {previousDates} {previousValue}
              </TooltipText>
            </TooltipRow>
          )}
        </TooltipContainer>
      }
    >
      <Block>
        <Title>{block.title}</Title>
        <Data>
          {isLoading ? (
            <Skeleton h={'20px'} />
          ) : (
            <>
              <Value>{value}</Value>
              {!block.hidePercent && (
                <Percent>
                  <PercentText $positive={positiveDynamics}>
                    {block.formatPrevious?.field ? previousValue : percentValue}
                  </PercentText>
                  {!block.formatPrevious?.hideArrow && positiveDynamics !== 'neutral' && (
                    <IconStyled $positive={positiveDynamics} />
                  )}
                  {block.formatPrevious?.extra && (
                    <PercentText $positive={positiveDynamics}>
                      {block.formatPrevious?.extra}
                    </PercentText>
                  )}
                </Percent>
              )}
            </>
          )}
        </Data>
      </Block>
    </FrontTooltip>
  );
};

const TooltipContainer = styled('div')`
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const TooltipRow = styled('div')`
  display: flex;
  gap: 4px;
  width: 100%;
  flex-direction: column;
`;

const TooltipText = styled('div')`
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  white-space: nowrap;
`;

const Block = styled('div')`
  display: flex;
  flex-direction: column;
`;
const Title = styled('div')`
  overflow: hidden;
  color: #5f676b;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.fonts.telka};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;
const Data = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Value = styled('div')`
  color: #121517;
  font-family: ${({ theme }) => theme.fonts.telkaExtended};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
`;
const Percent = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PercentText = styled('div')<{ $positive: Numbers.DynamicType }>`
  overflow: hidden;
  color: ${({ $positive }) => Numbers.dynamicsColors[$positive]};
  text-overflow: ellipsis;

  font-family: ${({ theme }) => theme.fonts.telka};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const IconStyled = styled(TrendIcon)<{ $positive: Numbers.DynamicType }>`
  width: 16px;
  height: 16px;
  color: ${({ $positive }) => Numbers.dynamicsColors[$positive]};
  transform: rotate(${({ $positive }) => ($positive !== 'negative' ? 0 : '90deg')});
`;
